import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const RevenuePeriodComparisonChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                return {
                    date,
                    revenue: metrics?.revenue ?? 0,
                };
            });
    }, [finance?.dailyBreakdown]);

    const totalRevenue = useMemo(
        () => chartData.reduce((sum, d) => sum + d.revenue, 0),
        [chartData]
    );

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: CHART_COLORS.revenue }}>
                {formatCompactNumber(totalRevenue, currencySymbol)}
            </span>
        ) : undefined,
    });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number) => [
                                formatCurrency(value, currencySymbol),
                                t('charts.revenue', 'Revenue'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Line
                            type="monotone"
                            dataKey="revenue"
                            name={t('charts.revenue', 'Revenue')}
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.revenue }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-primary opacity-40 px-4 pb-2">
                {t('charts.previousPeriodNote', 'Previous period comparison requires additional data')}
            </p>
        </div>
    );
};
